<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import type { EChartsOption } from 'echarts'
import { pageById } from '@/data/registry'
import { usePrototypeStore } from '@/stores/prototype'
import ProTablePage from '@/components/pro/ProTablePage.vue'
import EChart from '@/components/ui/EChart.vue'
import AppIcon from '@/components/AppIcon.vue'
import { formatNumber } from '@/utils/format'

const definition = pageById('health-monitoring')
if (!definition) throw new Error('健康监测页面定义缺失')

const store = usePrototypeStore()
const router = useRouter()
const db = store.db
const tableRef = ref<InstanceType<typeof ProTablePage> | null>(null)

const metrics = computed(() => db.health_metrics)
const alerts = computed(() => db.alerts)
const devices = computed(() => db.devices)
const users = computed(() => db.users)
const openAlerts = computed(() => alerts.value.filter(alert => ['pending', 'processing'].includes(String(alert.status))))
const abnormalMetrics = computed(() => metrics.value.filter(metric => Boolean(metric.is_abnormal)))

function metricCount(type: string, abnormal = true): number {
  return metrics.value.filter(metric => metric.metric_type === type && (!abnormal || Boolean(metric.is_abnormal))).length
}

const coverage = computed(() => {
  const covered = new Set(metrics.value.map(metric => String(metric.user_id))).size
  return users.value.length ? Number(((covered / users.value.length) * 100).toFixed(1)) : 0
})

const kpis = computed(() => [
  { key: 'alerts', label: '待处理异常预警', value: formatNumber(openAlerts.value.length), unit: '人', icon: 'alert', tone: 'danger', hint: `高风险 ${openAlerts.value.filter(alert => alert.severity === '高').length} 条` },
  { key: 'online', label: '在线设备数', value: formatNumber(devices.value.filter(device => device.status === 'online').length), unit: '台', icon: 'device', tone: 'info', hint: `共 ${devices.value.length} 台设备` },
  { key: 'bp', label: '血压异常', value: formatNumber(metricCount('血压')), unit: '人', icon: 'heart', tone: 'danger', hint: '近 24 小时上报' },
  { key: 'glucose', label: '血糖异常', value: formatNumber(metricCount('血糖')), unit: '人', icon: 'drop', tone: 'success', hint: '含空腹与餐后' },
  { key: 'heart', label: '心率异常', value: formatNumber(metricCount('心率')), unit: '人', icon: 'activity', tone: 'info', hint: '静息与运动心率' },
  { key: 'coverage', label: '监测覆盖率', value: `${coverage.value}%`, unit: '', icon: 'check-circle', tone: 'success', hint: '有健康上报的用户占比' },
])

const days = computed(() => Array.from({ length: 7 }, (_, index) => {
  const date = new Date('2026-10-17T12:00:00+08:00')
  date.setDate(date.getDate() - (6 - index))
  return { key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`, label: `${date.getMonth() + 1}/${date.getDate()}` }
}))

function trendFor(type: '血压' | '血糖'): Array<number | null> {
  return days.value.map(day => {
    const rows = metrics.value.filter(metric => metric.metric_type === type && String(metric.measured_at ?? '').slice(0, 10) === day.key)
    if (!rows.length) return null
    const values = rows.map(metric => Number(String(metric.metric_value ?? '').split('/')[0]))
    return Number((values.reduce((sum, value) => sum + value, 0) / values.length).toFixed(1))
  })
}

function lineOption(series: Array<{ name: string; data: Array<number | null>; color: string; area?: boolean }>, unit: string): EChartsOption {
  return {
    animationDuration: 480,
    grid: { left: 8, right: 18, top: 20, bottom: 24, containLabel: true },
    tooltip: { trigger: 'axis', backgroundColor: '#fff', borderColor: '#e8eef4', textStyle: { color: '#1f2d45', fontSize: 11 }, valueFormatter: value => `${value} ${unit}` },
    legend: { show: true, top: 0, right: 0, itemWidth: 7, itemHeight: 7, textStyle: { fontSize: 9, color: '#7183a1' } },
    xAxis: { type: 'category', boundaryGap: false, data: days.value.map(day => day.label), axisTick: { show: false }, axisLine: { lineStyle: { color: '#e8eef4' } }, axisLabel: { color: '#9aa8bd', fontSize: 9 } },
    yAxis: { type: 'value', scale: true, splitNumber: 4, axisLabel: { color: '#9aa8bd', fontSize: 9 }, splitLine: { lineStyle: { color: '#f0f4f8', type: 'dashed' } } },
    series: series.map((item, index) => ({
      name: item.name, type: 'line', smooth: 0.35, data: item.data, connectNulls: true, symbol: 'circle', symbolSize: 4,
      lineStyle: { color: item.color, width: 2 }, itemStyle: { color: item.color, borderColor: '#fff', borderWidth: 2 },
      areaStyle: item.area ? { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: `${item.color}30` }, { offset: 1, color: `${item.color}02` }] } } : undefined,
      z: index + 1,
    })),
  }
}

const bpOption = computed(() => lineOption([{ name: '收缩压', data: trendFor('血压'), color: '#f04444', area: true }], 'mmHg'))
const glucoseOption = computed(() => lineOption([{ name: '血糖', data: trendFor('血糖'), color: '#08b578', area: true }], 'mmol/L'))

const deviceStatus = computed(() => {
  const groups = ['online', 'offline', 'fault', 'unbound']
  const labels: Record<string, string> = { online: '在线设备', offline: '离线设备', fault: '故障设备', unbound: '未激活' }
  const colors: Record<string, string> = { online: '#08b578', offline: '#9aa8bd', fault: '#f5a623', unbound: '#8b7cf6' }
  return groups.map(key => ({ key, label: labels[key] as string, value: devices.value.filter(device => device.status === key).length, color: colors[key] as string }))
})

const deviceOption = computed<EChartsOption>(() => ({
  animationDuration: 480,
  tooltip: { trigger: 'item', backgroundColor: '#fff', borderColor: '#e8eef4', textStyle: { color: '#1f2d45', fontSize: 11 } },
  series: [{ type: 'pie', radius: ['58%', '80%'], center: ['50%', '50%'], label: { show: false }, itemStyle: { borderColor: '#fff', borderWidth: 2 }, data: deviceStatus.value.map(item => ({ name: item.label, value: item.value, itemStyle: { color: item.color } })) }],
  graphic: [
    { type: 'text', left: 'center', top: '41%', style: { text: formatNumber(devices.value.length), fill: '#0b1b3a', fontSize: 22, fontWeight: 700, fontFamily: 'DIN Alternate, sans-serif' } },
    { type: 'text', left: 'center', top: '55%', style: { text: '设备总数', fill: '#7183a1', fontSize: 9 } },
  ],
}))

const severityData = computed(() => [
  { name: '高风险', value: openAlerts.value.filter(alert => alert.severity === '高').length, color: '#f04444' },
  { name: '中风险', value: openAlerts.value.filter(alert => alert.severity === '中').length, color: '#f5a623' },
  { name: '低风险', value: openAlerts.value.filter(alert => alert.severity === '低').length, color: '#2f86eb' },
])

const severityOption = computed<EChartsOption>(() => ({
  animationDuration: 480,
  tooltip: { trigger: 'item', backgroundColor: '#fff', borderColor: '#e8eef4', textStyle: { color: '#1f2d45', fontSize: 11 } },
  series: [{ type: 'pie', radius: ['58%', '80%'], center: ['50%', '50%'], label: { show: false }, itemStyle: { borderColor: '#fff', borderWidth: 2 }, data: severityData.value.map(item => ({ name: item.name, value: item.value, itemStyle: { color: item.color } })) }],
  graphic: [
    { type: 'text', left: 'center', top: '41%', style: { text: formatNumber(openAlerts.value.length), fill: '#0b1b3a', fontSize: 22, fontWeight: 700, fontFamily: 'DIN Alternate, sans-serif' } },
    { type: 'text', left: 'center', top: '55%', style: { text: '今日预警', fill: '#7183a1', fontSize: 9 } },
  ],
}))

const focusUsers = computed(() => {
  const map = new Map<string, { id: string; name: string; age: unknown; severity: string; count: number; family: string }>()
  for (const alert of openAlerts.value) {
    const key = String(alert.user_id ?? alert.user_name)
    const current = map.get(key)
    if (current) current.count += 1
    else map.set(key, { id: key, name: String(alert.user_name), age: alert.age, severity: String(alert.severity), count: 1, family: String(alert.family_name ?? '') })
  }
  return [...map.values()].sort((left, right) => right.count - left.count).slice(0, 4)
})

const quickActions = [
  { label: '指派跟进', sub: '分配待处理预警', icon: 'users', tone: 'green', route: '/health/alerts' },
  { label: '联系家属', sub: '一键联系家庭成员', icon: 'phone', tone: 'blue', route: '/support/tickets' },
  { label: '生成报告', sub: '汇总健康指标', icon: 'report', tone: 'purple', route: '/health/reports' },
  { label: '设备管理', sub: '查看在线与故障', icon: 'device', tone: 'cyan', route: '/devices' },
  { label: '预警规则', sub: '调整阈值与通知', icon: 'rules', tone: 'amber', route: '/health/alert-rules' },
]

function go(path: string): void {
  void router.push(path)
}

function exportCurrent(): void {
  const target = tableRef.value as unknown as { exportData?: (scope: 'current' | 'filtered' | 'selected') => void } | null
  target?.exportData?.('current')
}
</script>

<template>
  <section class="health-page">
    <header class="page-header health-header">
      <div class="page-header__text">
        <div class="page-header__title-row">
          <h1 class="page-title">健康监测中心</h1>
          <span class="prototype-pill">原型数据</span>
        </div>
        <p class="page-subtitle">实时监控用户健康数据，及时发现异常风险，守护家庭健康安全</p>
      </div>
      <div class="page-header__actions">
        <button type="button" class="btn" @click="go('/health/alert-rules')"><AppIcon name="rules" :size="14" />预警规则</button>
        <button type="button" class="btn" @click="go('/devices')"><AppIcon name="device" :size="14" />设备管理</button>
        <button type="button" class="btn" @click="exportCurrent"><AppIcon name="export" :size="14" />导出数据</button>
      </div>
    </header>

    <section class="health-kpis" aria-label="健康监测核心指标">
      <article v-for="kpi in kpis" :key="kpi.key" class="health-kpi" :class="`health-kpi--${kpi.tone}`">
        <span class="health-kpi__icon"><AppIcon :name="kpi.icon" :size="19" /></span>
        <div class="health-kpi__body">
          <span class="health-kpi__label">{{ kpi.label }}</span>
          <strong>{{ kpi.value }}<small v-if="kpi.unit">{{ kpi.unit }}</small></strong>
          <span class="health-kpi__hint">{{ kpi.hint }}</span>
        </div>
        <span class="health-kpi__spark" aria-hidden="true"><i v-for="(height, index) in [32, 48, 40, 58, 52, 69, 61, 76]" :key="index" :style="{ height: `${height}%` }" /></span>
      </article>
    </section>

    <ProTablePage ref="tableRef" :definition="definition" embedded>
      <template #before-filters>
        <section class="health-charts" aria-label="健康趋势与分布">
          <article class="card health-chart">
            <header class="card__head"><div><h2 class="card__title">血压趋势分析</h2><p class="card__sub">最近7天平均收缩压 · mmHg</p></div><span class="tag tag--neutral">近7天</span></header>
            <EChart :option="bpOption" height="150px" aria-label="最近7天血压趋势折线图" />
          </article>
          <article class="card health-chart">
            <header class="card__head"><div><h2 class="card__title">血糖趋势分析</h2><p class="card__sub">最近7天平均血糖 · mmol/L</p></div><span class="tag tag--neutral">近7天</span></header>
            <EChart :option="glucoseOption" height="150px" aria-label="最近7天血糖趋势折线图" />
          </article>
          <article class="card health-chart health-chart--donut">
            <header class="card__head"><div><h2 class="card__title">设备在线情况</h2><p class="card__sub">设备状态分布</p></div><button type="button" class="card__link" @click="go('/devices')">查看详情 <AppIcon name="chevron-right" :size="12" /></button></header>
            <EChart :option="deviceOption" height="118px" aria-label="设备在线状态环图" />
            <div class="donut-legend"><span v-for="item in deviceStatus" :key="item.key"><i :style="{ background: item.color }" />{{ item.label }}<b>{{ item.value }}</b></span></div>
          </article>
          <article class="card health-chart health-chart--donut">
            <header class="card__head"><div><h2 class="card__title">预警等级分布</h2><p class="card__sub">待处理与处理中</p></div><button type="button" class="card__link" @click="go('/health/alerts')">查看详情 <AppIcon name="chevron-right" :size="12" /></button></header>
            <EChart :option="severityOption" height="118px" aria-label="预警等级分布环图" />
            <div class="donut-legend"><span v-for="item in severityData" :key="item.name"><i :style="{ background: item.color }" />{{ item.name }}<b>{{ item.value }}</b></span></div>
          </article>
        </section>
      </template>

      <template #after-table>
        <section class="health-bottom" aria-label="设备与重点用户">
          <article class="card health-bottom__panel">
            <header class="card__head"><div><h2 class="card__title">设备状态概览</h2><p class="card__sub">在线、离线、故障与未激活</p></div><button type="button" class="card__link" @click="go('/devices')">查看全部 <AppIcon name="chevron-right" :size="12" /></button></header>
            <div class="device-overview">
              <EChart :option="deviceOption" height="130px" aria-label="设备状态概览环图" />
              <div class="device-overview__list">
                <span v-for="item in deviceStatus" :key="item.key"><i :style="{ background: item.color }" />{{ item.label }}<b>{{ item.value }}</b><small>{{ devices.length ? Math.round(item.value / devices.length * 100) : 0 }}%</small></span>
              </div>
            </div>
          </article>

          <article class="card health-bottom__panel">
            <header class="card__head"><div><h2 class="card__title">重点关注用户</h2><p class="card__sub">近7天预警次数较多</p></div><button type="button" class="card__link" @click="go('/health/alerts')">查看全部 <AppIcon name="chevron-right" :size="12" /></button></header>
            <div class="focus-users">
              <button v-for="user in focusUsers" :key="user.id" type="button" class="focus-user" @click="go('/health/alerts')">
                <span class="avatar avatar--sm" :class="user.severity === '高' ? 'avatar--pink' : 'avatar--amber'">{{ user.name.slice(-2) }}</span>
                <span class="focus-user__main"><b>{{ user.name }}</b><small>{{ user.age }} 岁 · {{ user.family }}</small></span>
                <span class="tag" :class="user.severity === '高' ? 'tag--danger' : 'tag--warning'">{{ user.severity }}风险</span>
                <span class="focus-user__count">近7天 {{ user.count }} 次</span>
              </button>
              <div v-if="!focusUsers.length" class="empty-state" style="min-height: 140px"><h3 class="empty-state__title">暂无重点关注用户</h3><p class="empty-state__text">出现异常预警后会进入关注列表。</p></div>
            </div>
          </article>

          <article class="card health-bottom__panel">
            <header class="card__head"><div><h2 class="card__title">快捷操作</h2><p class="card__sub">健康运营高频入口</p></div></header>
            <div class="health-quick">
              <button v-for="action in quickActions" :key="action.label" type="button" class="health-quick__item" @click="go(action.route)">
                <span class="health-quick__icon" :class="`health-quick__icon--${action.tone}`"><AppIcon :name="action.icon" :size="16" /></span>
                <span><b>{{ action.label }}</b><small>{{ action.sub }}</small></span>
                <AppIcon name="chevron-right" :size="12" />
              </button>
            </div>
          </article>
        </section>
      </template>
    </ProTablePage>
  </section>
</template>

<style scoped>
.health-header { margin-bottom: 14px; }
.health-kpis { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 10px; margin-bottom: 14px; }
.health-kpi { position: relative; overflow: hidden; display: flex; align-items: flex-start; gap: 9px; min-height: 104px; padding: 13px 12px; border: 1px solid var(--c-border); border-radius: var(--radius-card); background: linear-gradient(145deg, #fff 0%, var(--hk-wash, #f3fcf8) 100%); box-shadow: var(--shadow-card); }
.health-kpi__icon { width: 32px; height: 32px; flex: 0 0 auto; border-radius: 9px; display: grid; place-items: center; background: var(--hk-soft, var(--c-brand-soft)); color: var(--hk, var(--c-brand-strong)); }
.health-kpi__body { min-width: 0; display: flex; flex-direction: column; flex: 1; }
.health-kpi__label { font-size: 10.5px; color: #53657e; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.health-kpi strong { margin-top: 4px; font-family: var(--font-num); font-size: 25px; line-height: 1; letter-spacing: -.4px; color: var(--c-ink); }
.health-kpi strong small { margin-left: 2px; font-family: var(--font-base); font-size: 10px; font-weight: 500; color: var(--c-muted); }
.health-kpi__hint { margin-top: 7px; font-size: 9px; color: var(--c-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.health-kpi__spark { position: absolute; right: 8px; bottom: 10px; width: 52px; height: 30px; display: flex; align-items: flex-end; gap: 2px; opacity: .3; }
.health-kpi__spark i { flex: 1; border-radius: 1px 1px 0 0; background: var(--hk, var(--c-brand)); }
.health-kpi--danger { --hk: #d33a3a; --hk-soft: #feecec; --hk-wash: #fffafa; }
.health-kpi--info { --hk: #2f7dcc; --hk-soft: #e9f3fe; --hk-wash: #f5faff; }
.health-kpi--success { --hk: #078e64; --hk-soft: #e3f8ef; --hk-wash: #f3fcf8; }
.health-charts { display: grid; grid-template-columns: minmax(0, 1.16fr) minmax(0, 1.16fr) minmax(0, .86fr) minmax(0, .86fr); gap: 10px; margin-bottom: 12px; }
.health-chart { min-width: 0; overflow: hidden; padding-bottom: 4px; }
.health-chart--donut :deep(.chart-box) { margin-top: -4px; }
.donut-legend { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 3px 8px; padding: 0 12px 8px; }
.donut-legend span { display: flex; align-items: center; gap: 4px; font-size: 8.5px; color: var(--c-muted); }
.donut-legend i { width: 5px; height: 5px; border-radius: 50%; }
.donut-legend b { margin-left: auto; font-family: var(--font-num); font-size: 9px; color: var(--c-text); }
.health-bottom { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr) minmax(0, 1fr); gap: 12px; margin-top: 14px; }
.health-bottom__panel { min-width: 0; overflow: hidden; padding-bottom: 8px; }
.device-overview { display: grid; grid-template-columns: 150px minmax(0, 1fr); gap: 0; align-items: center; padding: 0 8px 4px; }
.device-overview__list { display: grid; gap: 6px; }
.device-overview__list span { display: flex; align-items: center; gap: 5px; font-size: 9px; color: var(--c-muted); }
.device-overview__list i { width: 6px; height: 6px; border-radius: 50%; }
.device-overview__list b { margin-left: auto; font-family: var(--font-num); font-size: 10px; color: var(--c-text); }
.device-overview__list small { width: 28px; text-align: right; font-size: 8px; color: var(--c-faint); }
.focus-users { padding: 0 16px 4px; }
.focus-user { width: 100%; display: flex; align-items: center; gap: 7px; padding: 7px 0; border: 0; border-top: 1px solid var(--c-divider); background: transparent; text-align: left; }
.focus-user:first-child { border-top: 0; }
.focus-user__main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.focus-user__main b { font-size: 10.5px; color: var(--c-text); }
.focus-user__main small { margin-top: 1px; font-size: 8.5px; color: var(--c-faint); }
.focus-user__count { font-size: 8px; color: var(--c-faint); white-space: nowrap; }
.focus-user .tag { font-size: 8px; height: 18px; padding: 0 5px; }
.health-quick { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px; padding: 0 16px 4px; }
.health-quick__item { display: flex; align-items: center; gap: 6px; padding: 7px; border: 1px solid var(--c-border); border-radius: 8px; background: #fff; text-align: left; }
.health-quick__item:hover { border-color: var(--c-success-border); background: var(--c-brand-mist); }
.health-quick__item > span:nth-child(2) { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.health-quick__item b { font-size: 9.5px; color: var(--c-text); }
.health-quick__item small { font-size: 8px; color: var(--c-faint); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.health-quick__icon { width: 25px; height: 25px; flex: 0 0 auto; border-radius: 7px; display: grid; place-items: center; }
.health-quick__icon--green { background: var(--c-brand-soft); color: var(--c-brand-strong); }
.health-quick__icon--blue { background: var(--c-info-soft); color: var(--c-info); }
.health-quick__icon--purple { background: var(--c-purple-soft); color: var(--c-purple); }
.health-quick__icon--cyan { background: var(--c-cyan-soft); color: var(--c-cyan); }
.health-quick__icon--amber { background: var(--c-warning-soft); color: #b47710; }
@media (max-width: 1439px) {
  .health-kpis { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .health-charts { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 1023px) {
  .health-kpis { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .health-bottom { grid-template-columns: minmax(0, 1fr); }
}
@media (max-width: 767px) {
  .health-charts { grid-template-columns: minmax(0, 1fr); }
}
</style>
